import { NewUseUpdatedCart } from 'components/molecules/Cart/hooks';
import QUERY_KEYS from 'network/config/queryKeys';
import { getAllAddress } from 'network/services/address';
import { revalidateTier1Cart } from 'network/services/goods';
import { clearMadinaPendingPayment, madinaCheckout, removeMadinaCartItem, startMadinaCardCheckout } from 'network/services/madina';
import { submitOrder } from 'network/services/order';
import { getCustomer } from 'network/services/profile';
import { getMadinaErrorMessage } from 'network/utils/madinaResponse';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { branchIdAtom } from 'store/atoms/branchAtom';
import { cartListAtom } from 'store/atoms/cartAtom';
import { DEFAULT_CHECKOUT_DETAILS, checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import checkoutVariablesAtom from 'store/atoms/checkoutVariables';
import modalAtom from 'store/atoms/modals';
import { userRoleAtom } from 'store/atoms/userAtom';
import { PaystackSuccess, SuccessPaymentArgs } from 'TYPES/api.body';
import type { MadinaPaymentType } from 'TYPES/madina';
import { getStoredBranchId, setStoredBranch } from 'utils/branch';
import { resolveCheckoutCartOrder } from 'utils/checkoutCart';
import {
  clearCardIdempotencyKey,
  getMadinaBrowserCallbackUrl,
  getOrCreateCardIdempotencyKey,
  isLocalCallbackUrl,
  isOnionPayWebhookUrl,
  type Tier1PaymentSelection
} from 'utils/madinaPayment';
import { v4 as uuidv4 } from 'uuid';

export default function usePayment() {
  const { orderId } = useParams();
  const [user, checkNewUser] = useState('');
  const serverCartQuery = NewUseUpdatedCart();
  const serverCart = serverCartQuery?.data;
  const cartOrder = resolveCheckoutCartOrder(serverCart, orderId);
  const subTotal = cartOrder?.totalAmount || 0;
  const submissionInProgress = useRef(false);
  const processedPaymentRefs = useRef(new Set());
  const userRole = useRecoilValue(userRoleAtom);
  const storedBranchId = useRecoilValue(branchIdAtom);
  const routeBranchId = Number(orderId);
  const routeBranch = Number.isFinite(routeBranchId) && routeBranchId > 1 ? routeBranchId : null;
  const branchId = routeBranch ?? getStoredBranchId() ?? storedBranchId;
  const useTier1 = userRole !== 'BUSINESS' && !!branchId;

  useEffect(() => {
    return checkNewUser(localStorage.getItem('newuser') ?? 'false');
  }, []);

  const go = useNavigate();
  const queryClient = useQueryClient();
  const { data: addressData } = useQuery({
    queryFn: getAllAddress,
    queryKey: QUERY_KEYS.CUSTOMER_ADDRESSES
  });

  const defaultAddress = React.useMemo(() => {
    const addresses = addressData?.data ? addressData.data : [];
    const defAddr = addresses.find(item => item.status === true);
    if (!defAddr && addresses.length > 0) {
      return addresses[0];
    }
    return defAddr;
  }, [addressData]);

  const setModalState = useSetRecoilState(modalAtom);
  const setCartList = useSetRecoilState(cartListAtom);
  const [checkoutDetails, setCheckoutDetails] = useRecoilState(checkoutDetailsAtom);
  const [checkoutVariables, setCheckoutVariables] = useRecoilState(checkoutVariablesAtom);
  const { mutateAsync: _submitOrder, isLoading: isSubmitOrderLoading } = useMutation({ mutationFn: submitOrder });
  const { mutateAsync: tier1Checkout, isLoading: isTier1CheckoutLoading } = useMutation({ mutationFn: madinaCheckout });
  const { data: customerProfile } = useQuery({
    queryFn: getCustomer,
    queryKey: QUERY_KEYS.CUSTOMER_PROFILE
  });

  const handleOrderSuccess = async () => {
    enqueueSnackbar('Order Placed Successfully', { variant: 'success' });
    localStorage.removeItem('cart');
    setCartList({});
    setCheckoutVariables(curr => ({
      ...curr,
      checkoutLoading: isSubmitOrderLoading || isTier1CheckoutLoading,
      pointsUsed: { points: 0, pointNairaEquivalent: 0 },
      deliveryOccurrence: 'None'
    }));
    go('/account/orders', { replace: true });
    setCheckoutDetails(DEFAULT_CHECKOUT_DETAILS);
    queryClient.invalidateQueries([QUERY_KEYS.CART]);
    queryClient.invalidateQueries([QUERY_KEYS.MADINA_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.ALL_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.COMPLETE_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.ONGOING_ORDERS]);
    queryClient.invalidateQueries([QUERY_KEYS.CANCELED_ORDERS]);
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: true,
      paymentFailureModalOpen: false,
      transactionInProgressModalOpen: false
    }));
  };

  const handleOrderFailure = (error: Error) => {
    enqueueSnackbar(error.message, { variant: 'error' });
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: false,
      paymentFailureModalOpen: true,
      paymentFailureMessage: error.message,
      transactionInProgressModalOpen: false
    }));
    setCheckoutVariables(curr => ({
      ...curr,
      pointsUsed: { points: 0, pointNairaEquivalent: 0 }
    }));
  };

  const processTier1Checkout = async (selection: Tier1PaymentSelection | MadinaPaymentType) => {
    const paymentType = typeof selection === 'string' ? selection : selection.paymentType;

    if (!branchId) {
      enqueueSnackbar('Please select a branch before checkout', { variant: 'warning' });
      go('/branches');
      return;
    }

    if (checkoutDetails.deliveryMethod === 'homedelivery' && !defaultAddress?.id) {
      enqueueSnackbar('Please add a delivery address', { variant: 'error' });
      return;
    }

    setModalState(c => ({ ...c, transactionInProgressModalOpen: true, paymentModalOpen: false }));

    try {
      if (branchId) setStoredBranch(branchId);
      let cart = await revalidateTier1Cart();
      const unavailable = cart.items.filter(item => item.available === false);
      if (unavailable.length > 0) {
        for (const item of unavailable) {
          const lineId = item.cart_item_id ?? item.id;
          if (lineId) await removeMadinaCartItem(branchId, lineId);
        }
        queryClient.invalidateQueries([QUERY_KEYS.CART]);
        const names = unavailable
          .map(item => item.product?.name || `product ${item.product_id}`)
          .join(', ');
        if (unavailable.length >= cart.items.length) {
          throw new Error(
            `${names} is not sold at this store. Go back to the shop for this store and add a product from there.`
          );
        }
        enqueueSnackbar(`Removed items this store does not sell: ${names}`, { variant: 'warning' });
        cart = await revalidateTier1Cart();
      }
      if (cart.all_available === false) {
        const names = cart.items
          .filter(item => item.available === false)
          .map(item => item.product?.name || `product ${item.product_id}`);
        throw new Error(
          names.length
            ? `${names.join(', ')} is not available at this store. Remove it from the cart and try again.`
            : 'Some items in your cart are not available at this store. Remove them and try again.'
        );
      }

      const cardCallbackUrl = paymentType === 'card' ? getMadinaBrowserCallbackUrl() : undefined;

      if (paymentType === 'card' && cardCallbackUrl) {
        if (isOnionPayWebhookUrl(cardCallbackUrl)) {
          throw new Error(
            'Payment callback must be your storefront URL (e.g. https://staging.madina.ng/checkout/callback), not the ERP webhook.'
          );
        }
        if (isLocalCallbackUrl(cardCallbackUrl)) {
          throw new Error(
            'OnionPay requires a public HTTPS callback URL. Set REACT_APP_APPLICATION_DOMAIN or REACT_APP_PAYMENT_CALLBACK_URL and restart the dev server.'
          );
        }
        if (!cardCallbackUrl.startsWith('https://')) {
          throw new Error('OnionPay callback URL must use HTTPS.');
        }
      }

      const response = await tier1Checkout({
        branch_id: branchId,
        payment_type: paymentType,
        idempotency_key: paymentType === 'card' ? getOrCreateCardIdempotencyKey() : uuidv4(),
        delivery_method: checkoutDetails.deliveryMethod === 'pickup' ? 'pickup' : 'delivery',
        address_id: checkoutDetails.deliveryMethod === 'pickup' ? undefined : defaultAddress?.id,
        callback_url: cardCallbackUrl,
        driver_note: checkoutDetails.driverNote || checkoutVariables.driverNote
      });

      const paymentStatus = String(
        response.payment?.payment_status ?? response.order?.payment_status ?? ''
      ).toUpperCase();

      if (paymentType === 'wallet' && paymentStatus && !['PAID', 'SUCCESS', 'COMPLETED'].includes(paymentStatus)) {
        throw new Error('Insufficient wallet balance');
      }

      if (paymentType === 'wallet' || paymentType === 'pod' || paymentStatus === 'PAID' || paymentStatus === 'POD') {
        if (paymentType === 'card' || paymentStatus === 'PAID') {
          clearCardIdempotencyKey();
        }
        await handleOrderSuccess();
        return;
      }

      if (paymentType === 'transfer') {
        setModalState(c => ({ ...c, transactionInProgressModalOpen: false }));
        enqueueSnackbar('Transfer instructions sent. Complete payment using the order reference.', {
          variant: 'info'
        });
        go(`/account/orders/${response.order.id}`, { replace: true });
        return;
      }

      if (paymentType === 'card') {
        await startMadinaCardCheckout(response.payment, response.order.id, {
          duplicate: response.duplicate === true
        });
        return;
      }

      await handleOrderSuccess();
    } catch (error) {
      if (paymentType === 'card') {
        clearCardIdempotencyKey();
        clearMadinaPendingPayment();
      }
      handleOrderFailure(new Error(getMadinaErrorMessage(error)));
    } finally {
      setModalState(c => ({ ...c, transactionInProgressModalOpen: false }));
    }
  };

  const initiateBusinessPostPayment = () => {
    if (checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier === undefined) {
      enqueueSnackbar('Please select a carrier', { variant: 'error' });
      return;
    }

    setModalState(c => ({ ...c, transactionInProgressModalOpen: true }));

    let grandTotal = subTotal + (checkoutDetails.shippingFee ?? 0) - checkoutVariables.pointsUsed.pointNairaEquivalent;

    _submitOrder({
      ...checkoutDetails,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id,
      has10PercentDiscount: user === 'true' ? true : false,
      deliveryAgentId: checkoutVariables.selectedCarrier?.carrierId
        ? parseInt(checkoutVariables.selectedCarrier.carrierId)
        : undefined,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta
          }
          : null,
      points: checkoutVariables.pointsUsed.points,
      customerId: customerProfile?.data.id,
      grandTotal,
      total: grandTotal,
      subAmount: user === 'true' ? subTotal - subTotal * 0.1 : subTotal,
      driverNote: checkoutDetails.driverNote && checkoutDetails.driverNote !== '' ? checkoutDetails.driverNote : 'null',
      paymentType: 'Post Payment',
      paymentTrxRef: uuidv4(),
      paymentMode: 'post_payment',
      customerType: userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR',
      request_token: checkoutDetails.request_token
    })
      .then((data: any) => {
        if (data.data.paymentStatus === 'Success' || data.data.paymentStatus === 'success') {
          handleOrderSuccess();
        } else {
          handleOrderFailure(new Error('Payment not successful'));
        }
      })
      .catch((err: any) => handleOrderFailure(err));
  };

  const handlePaymentSuccess = (args: SuccessPaymentArgs) => {
    if (useTier1) {
      processTier1Checkout('card');
      return;
    }

    if (args.reference && processedPaymentRefs.current.has(args.reference)) {
      return;
    }

    if (submissionInProgress.current) {
      return;
    }

    if (args.reference) {
      processedPaymentRefs.current.add(args.reference);
    }
    submissionInProgress.current = true;

    _submitOrder({
      ...checkoutDetails,
      request_token: checkoutVariables?.request_token || '',
      driverNote: checkoutVariables.driverNote,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta,
            request_token: checkoutVariables.request_token
          }
          : null,
      has10PercentDiscount: user === 'true' ? true : false,
      customerType: userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR',
      recurringDelivery: !(checkoutVariables.deliveryOccurrence === 'None'),
      recurringDeliveryEndDate:
        checkoutVariables.deliveryOccurrence === 'None' ? undefined : checkoutDetails.recurringDeliveryEndDate,
      deliverySchedule: checkoutDetails.deliveryMethod === 'pickup' ? 'Immediately' : checkoutDetails.deliverySchedule,
      status: args.status,
      paymentTrxRef: args.reference ?? uuidv4(),
      paymentMode: args.paymentMode,
      paymentType: args.paymentType,
      paymentStatus: args.status as any,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id
    })
      .then((data: any) => {
        if (data.data.paymentStatus === 'SUCCESS' || data.data.paymentStatus === 'Success') {
          handleOrderSuccess();
        } else {
          handleOrderFailure(new Error('Payment not successful'));
        }
      })
      .catch((err: any) => {
        if (args.reference) {
          processedPaymentRefs.current.delete(args.reference);
        }
        submissionInProgress.current = false;
        handleOrderFailure(err);
      })
      .finally(() => {
        submissionInProgress.current = false;
      });
  };

  const onWalletSuccess = () => {
    if (useTier1) {
      processTier1Checkout('wallet');
      return;
    }

    setModalState(c => ({
      ...c,
      transactionInProgressModalOpen: true,
      paymentModalOpen: false
    }));
    handlePaymentSuccess({
      status: 'Success',
      paymentMode: 'Wallet',
      paymentType: 'Madina Wallet'
    });
  };

  const onSuccess = (reference?: PaystackSuccess) => {
    if (useTier1) {
      processTier1Checkout('card');
      return;
    }

    setModalState(c => ({
      ...c,
      transactionInProgressModalOpen: true,
      paymentModalOpen: false
    }));
    if (reference !== undefined) {
      handlePaymentSuccess({
        paymentMode: 'PayStack',
        reference: reference.reference,
        status: reference.status,
        paymentType: 'Credit Card'
      });
    } else {
      handleOrderFailure(new Error('No reference found'));
    }
  };

  const onClose = () => {
    setModalState(curr => ({
      ...curr,
      paymentModalOpen: false,
      paymentSuccessModalOpen: false,
      paymentFailureModalOpen: false,
      transactionInProgressModalOpen: false
    }));
  };

  const initiatePayment = () => {
    if (useTier1) {
      setModalState(curr => ({ ...curr, paymentModalOpen: true }));
      return;
    }

    if (checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier === undefined) {
      enqueueSnackbar('Please select a carrier', { variant: 'error' });
      return;
    }

    let grandTotal = subTotal + (checkoutDetails.shippingFee ?? 0) - checkoutVariables.pointsUsed.pointNairaEquivalent;
    setCheckoutDetails(curr => ({ ...curr, grandTotal }));
    setCheckoutDetails(prev => ({
      ...prev,
      request_token: checkoutVariables.request_token || '',
      deliveryAgentId: checkoutVariables.selectedCarrier?.courier_id
        ? parseInt(checkoutVariables.selectedCarrier.courier_id)
        : undefined,
      logistic:
        checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier !== undefined
          ? {
            carrierId: checkoutVariables.selectedCarrier.courier_id,
            serviceCode: checkoutVariables.selectedCarrier.service_code,
            carrierName: checkoutVariables.selectedCarrier.courier_name,
            carrierLogo: checkoutVariables.selectedCarrier.courier_logo,
            amount: checkoutVariables.selectedCarrier.total,
            estimatedDaysOfDelivery: checkoutVariables.selectedCarrier.delivery_eta,
            request_token: checkoutVariables.request_token
          }
          : null,
      points: checkoutVariables.pointsUsed.points,
      customerId: customerProfile?.data.id,
      grandTotal,
      total: grandTotal,
      subAmount: user === 'true' ? subTotal - subTotal * 0.1 : subTotal,
      orderId: cartOrder?.id,
      locationId: cartOrder?.locationId,
      addressId: defaultAddress?.id
    }));
    setModalState(curr => ({ ...curr, paymentModalOpen: true }));
  };

  return {
    onClose,
    onSuccess,
    initiatePayment,
    onWalletSuccess,
    handleOrderSuccess,
    handleOrderFailure,
    initiateBusinessPostPayment,
    processTier1Checkout,
    useTier1,
    isTier1CheckoutLoading
  };
}
